import {union, without} from 'lodash';

import {useAppSelector} from '../../../../helpers/reduxHelpers';
import {CompanyFeaturesEnum} from '../../../../models/api/companyModel';
import {RulesNamespaceTypesEnum} from '../../../../models/api/ruleModel';
import {getFindHasCompanyFeature} from '../../../../store/data/company/companySelectors';
import {accountRules} from '../../accountRulesModel';
import {advancedRules, upgradedRules2021} from '../../advancedRulesModel';
import {RuleActionTypesEnum} from '../../ruleActionModel';
import {RuleConditionTypesEnum} from '../../ruleConditionModel';
import {triggersFromEvents} from '../../ruleTriggerHelpers';
import {RuleTriggerEventTypes, RuleTriggersTypesEnum} from '../../ruleTriggerModel';

interface BundleService {
  unavailableTriggers: ReadonlyArray<RuleTriggersTypesEnum>;
  unavailableConditions: ReadonlyArray<RuleConditionTypesEnum>;
  unavailableActions: ReadonlyArray<RuleActionTypesEnum>;
}

type UnavailableComponentsTypes = RuleConditionTypesEnum | RuleActionTypesEnum | RuleTriggerEventTypes;

export function useBundleService(): BundleService {
  const findHasCompanyFeature = useAppSelector(getFindHasCompanyFeature);

  function getUnavailableComponents<R extends UnavailableComponentsTypes>(
    componentType: RulesNamespaceTypesEnum,
  ): R[] {
    const hasAdvancedRulesFeature = findHasCompanyFeature(CompanyFeaturesEnum.ADVANCED_RULES);
    const hasAccountRulesFeature = findHasCompanyFeature(CompanyFeaturesEnum.ACCOUNT_RULES);
    const hasUpgradedRulesFeature = findHasCompanyFeature(CompanyFeaturesEnum.UPGRADED_RULES);

    if (hasAdvancedRulesFeature && hasAccountRulesFeature) {
      return [];
    }

    // Account Rule Checks.
    const unavailableAccountRules = hasAccountRulesFeature ? [] : accountRules[componentType];
    // Advanced Rule Checks.
    const unavailableAdvancedRules = computeUnavailableAdvancedRules(
      hasAdvancedRulesFeature,
      hasUpgradedRulesFeature,
      componentType,
    );

    return union(unavailableAdvancedRules, unavailableAccountRules) as R[];
  }

  const unavailableTriggers = triggersFromEvents(
    getUnavailableComponents<RuleTriggerEventTypes>(RulesNamespaceTypesEnum.EVENTS),
  );

  const unavailableConditions = getUnavailableComponents<RuleConditionTypesEnum>(
    RulesNamespaceTypesEnum.COMPOUND_CONDITIONS,
  );

  /*
   *  'assign' and 'random_assign' share the same UI dropdown element, but are
   *  available in different plans. By removing 'random_assign' from the list of
   *  unavailable actions for the pre 2021 upgraded rules that are used to disable
   *  UI elements, we are able to still access the UI element for 'assign', but can
   *  rely on the advanced feature gate to stop the customer from sucessfully making
   *  the unavailable 'random_assign' rules.
   */
  const unavailableActionsComponents = getUnavailableComponents<RuleActionTypesEnum>(
    RulesNamespaceTypesEnum.ACTIONS,
  );
  const unavailableActions = findHasCompanyFeature(CompanyFeaturesEnum.UPGRADED_RULES)
    ? unavailableActionsComponents.filter((action) => action !== RuleActionTypesEnum.RANDOM_ASSIGN)
    : unavailableActionsComponents;

  return {
    unavailableTriggers,
    unavailableConditions,
    unavailableActions,
  };
}

/*
 * Helpers.
 */

function computeUnavailableAdvancedRules(
  hasAdvancedRules: boolean,
  hasUpgradedRules: boolean,
  componentType: RulesNamespaceTypesEnum,
) {
  if (hasAdvancedRules) {
    return [];
  }

  const advancedRulesForComponent = advancedRules[componentType];
  const updatedRulesForComponent = upgradedRules2021[componentType];
  if (hasUpgradedRules) {
    return without(advancedRulesForComponent, ...updatedRulesForComponent);
  }
  return advancedRulesForComponent;
}
